import { Alert, Button, Center, Group, Loader, Modal, Paper, Stack, Text, Title } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { GoogleIdentityButton } from "atlas_forecasting_app/components/common/google_identity_button"

const AUTH_PROVIDERS = [
  { key: "google", kind: "google_identity" },
]

function joinClassNames(...classNames) {
  return classNames.filter(Boolean).join(" ")
}

export function PageHeader({ eyebrow, title, subtitle, action, variant = "default", titleOrder = 1, className }) {
  return (
    <div className={joinClassNames("af-page-header", `af-page-header--${variant}`, className)}>
      <div className="af-page-header__content">
        <Stack gap={variant === "hero" ? "sm" : 6}>
          {eyebrow ? (
            <Text size="sm" fw={700} className="af-page-eyebrow">
              {eyebrow}
            </Text>
          ) : null}
          {title ? (
            typeof title === "string" ? (
              <Title order={titleOrder} className="af-page-title">
                {title}
              </Title>
            ) : (
              title
            )
          ) : null}
          {subtitle ? (
            typeof subtitle === "string" ? (
              <Text className="af-page-subtitle">{subtitle}</Text>
            ) : (
              subtitle
            )
          ) : null}
        </Stack>
      </div>
      {action ? <div className="af-page-header__action">{action}</div> : null}
    </div>
  )
}

export function SectionCard({ title, subtitle, action, children, variant = "default", className, contentClassName, ...paperProps }) {
  return (
    <Paper withBorder radius="xl" shadow="xs" p="lg" className={joinClassNames("af-section-card", `af-section-card--${variant}`, className)} {...paperProps}>
      <Stack gap="md" className={joinClassNames("af-section-card__stack", contentClassName)}>
        {title || subtitle || action ? (
          <Group justify="space-between" align="flex-start" gap="md" className="af-section-card__header">
            <Stack gap={4} className="af-section-card__heading">
              {title ? (
                typeof title === "string" ? (
                  <Title order={3} className="af-section-card__title">
                    {title}
                  </Title>
                ) : (
                  title
                )
              ) : null}
              {subtitle ? (typeof subtitle === "string" ? <Text className="af-section-card__subtitle">{subtitle}</Text> : subtitle) : null}
            </Stack>
            {action ? <div className="af-section-card__action">{action}</div> : null}
          </Group>
        ) : null}
        {children}
      </Stack>
    </Paper>
  )
}

export function ErrorAlert({ message }) {
  if (!message) return null
  return (
    <Alert color="red" title="Something went wrong" variant="light">
      {message}
    </Alert>
  )
}

export function LoadingState({ label = "Loading..." }) {
  return (
    <Center py="xl" className="af-loading-state">
      <Group gap="sm">
        <Loader size="sm" />
        <Text className="af-loading-state__label">{label}</Text>
      </Group>
    </Center>
  )
}

export function EmptyState({ title = "Nothing here yet", message, action, variant = "default" }) {
  return (
    <Paper withBorder radius="xl" p="lg" className={joinClassNames("af-empty-state", `af-empty-state--${variant}`)}>
      <Stack gap="sm" align="flex-start">
        <Title order={4} className="af-empty-state__title">
          {title}
        </Title>
        <Text className="af-empty-state__body">{message}</Text>
        {action}
      </Stack>
    </Paper>
  )
}

export function CenteredMessage({ eyebrow, title, body, action, variant = "default" }) {
  return (
    <Center mih={360}>
      <Paper withBorder radius="xl" shadow="xs" p="xl" maw={560} w="100%" className={joinClassNames("af-centered-message", `af-centered-message--${variant}`)}>
        <Stack gap="md" align="flex-start">
          {eyebrow ? (
            <Text size="sm" fw={700} className="af-page-eyebrow">
              {eyebrow}
            </Text>
          ) : null}
          <Title order={2} className="af-centered-message__title">
            {title}
          </Title>
          <Text className="af-centered-message__body">{body}</Text>
          {action}
        </Stack>
      </Paper>
    </Center>
  )
}

export function AuthModalButton({
  authInfo,
  onAuthResult,
  label = "Log in",
  fullWidth = false,
  variant = "filled",
  size = "md",
  modalTitle = "Sign up or Log in",
  modalBody = "Choose an authentication provider to access Atlas Forecasting.",
  origin,
  leftSection,
}) {
  const [opened, { open, close }] = useDisclosure(false)
  const providers = AUTH_PROVIDERS.map((provider) => {
    if (provider.kind === "google_identity" && authInfo?.google_client_id) {
      return (
        <GoogleIdentityButton
          key={provider.key}
          clientId={authInfo.google_client_id}
          origin={origin}
          fullWidth
          onAuthResult={(data) => {
            close()
            onAuthResult?.(data)
          }}
        />
      )
    }

    return null
  }).filter(Boolean)

  return (
    <>
      <Button fullWidth={fullWidth} variant={variant} size={size} leftSection={leftSection} onClick={open}>
        {label}
      </Button>

      <Modal opened={opened} onClose={close} title={modalTitle} centered>
        <Stack gap="md">
          <Text c="dimmed">{modalBody}</Text>
          {providers.length > 0 ? providers : <Text c="dimmed">No authentication providers are currently available.</Text>}
        </Stack>
      </Modal>
    </>
  )
}
